'use client'

import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react'

interface MockupScale {
  wrapperRef: RefObject<HTMLDivElement | null>
  innerRef: RefObject<HTMLDivElement | null>
  innerStyle: CSSProperties
  wrapperStyle: CSSProperties
  ready: boolean
}

export const useMockupScale = (): MockupScale => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [innerStyle, setInnerStyle] = useState<CSSProperties>({})
  const [wrapperStyle, setWrapperStyle] = useState<CSSProperties>({ height: 0 })
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const inner = innerRef.current
    if (!wrapper || !inner) return

    let raf = 0
    const update = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const naturalWidth = inner.scrollWidth
        const naturalHeight = inner.scrollHeight
        if (naturalWidth === 0 || naturalHeight === 0) return
        const available = wrapper.clientWidth
        const scale = Math.min(1, available / naturalWidth)
        setInnerStyle({
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          width: naturalWidth,
          height: naturalHeight
        })
        setWrapperStyle({ height: naturalHeight * scale })
        setReady(true)
      })
    }

    update()

    const wrapperObserver = new ResizeObserver(update)
    wrapperObserver.observe(wrapper)
    const innerObserver = new ResizeObserver(update)
    innerObserver.observe(inner)

    return () => {
      wrapperObserver.disconnect()
      innerObserver.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [])

  return { wrapperRef, innerRef, innerStyle, wrapperStyle, ready }
}
